import { useState, useEffect, useCallback } from "react";

const CG_BASE = "https://api.coingecko.com/api/v3";
const CAPITAL = 200000;

const NARRATIVES = {
  "hyperliquid": "On-chain Derivatives", "solana": "Layer-1 / DeFi",
  "near": "AI + Layer-1", "ondo-finance": "Real World Assets",
  "pump-fun": "Meme Launchpad", "chainlink": "Oracle Infrastructure",
  "render-token": "AI / GPU Computing", "injective-protocol": "DeFi / Cosmos",
  "sui": "Layer-1 / Move VM", "aptos": "Layer-1 / Move VM",
  "the-graph": "Data Infrastructure", "fetch-ai": "AI Agents",
  "bittensor": "Decentralised AI", "avalanche-2": "Layer-1 / Subnets",
  "polkadot": "Layer-0 / Interop", "arbitrum": "Ethereum L2",
  "optimism": "Ethereum L2", "stacks": "Bitcoin L2",
  "sei-network": "High-Performance L1", "ton": "Telegram Ecosystem",
  "bitcoin": "Store of Value", "ethereum": "Smart Contract Platform",
  "ripple": "Cross-border Payments", "cardano": "Proof-of-Stake L1",
  "dogecoin": "Meme / Payments", "shiba-inu": "Meme Token",
  "pepe": "Meme Token", "uniswap": "DEX Protocol",
  "aave": "DeFi Lending", "morpho": "DeFi Lending",
};

const RISK_MAP = {
  "hyperliquid": "Medium", "solana": "Medium", "near": "Medium-High",
  "ondo-finance": "Medium-High", "pump-fun": "High", "chainlink": "Low-Medium",
  "render-token": "Medium", "injective-protocol": "Medium-High",
  "sui": "Medium", "aptos": "Medium-High", "bitcoin": "Low-Medium",
  "ethereum": "Low-Medium", "ripple": "Medium", "cardano": "Medium",
  "dogecoin": "High", "shiba-inu": "High", "pepe": "High",
  "ton": "Medium", "arbitrum": "Medium", "optimism": "Medium",
  "uniswap": "Medium", "aave": "Medium", "morpho": "Medium-High",
};

const RISK_COLOR = { "Low-Medium": "#4CAF7D", "Medium": "#78AADF", "Medium-High": "#F0A500", "High": "#E05C5C" };
const RISK_BG = { "Low-Medium": "#0D2A1A", "Medium": "#0D1F3A", "Medium-High": "#2A1A00", "High": "#2A0D0D" };

// Strategy allocation weights based on momentum score
const STRATEGY_WEIGHTS = [0.28, 0.24, 0.20, 0.16, 0.12];
const STOP_LOSS = { "Low-Medium": 15, "Medium": 20, "Medium-High": 22, "High": 25 };
const TP1 = { "Low-Medium": 30, "Medium": 50, "Medium-High": 70, "High": 100 };
const TP2 = { "Low-Medium": 60, "Medium": 100, "Medium-High": 150, "High": 200 };

function fmt(n) {
  if (!n && n !== 0) return "—";
  if (n >= 1e9) return "$" + (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return "$" + (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return "$" + (n / 1e3).toFixed(1) + "K";
  if (n >= 1) return "$" + n.toFixed(2);
  return "$" + n.toFixed(4);
}

function fmtINR(n) {
  if (!n) return "₹0";
  if (n >= 1e5) return "₹" + (n / 1e5).toFixed(1) + "L";
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

function fmtPct(n) {
  if (!n && n !== 0) return "—";
  return (n >= 0 ? "+" : "") + n.toFixed(2) + "%";
}

/*
  COMPOSITE MOMENTUM SCORE — 6 factors, 100-point scale
  ─────────────────────────────────────────────────────
  Factor 1: 7d price trend       (max 40 pts) — PRIMARY signal. Negative 7d = hard disqualifier via filter.
  Factor 2: 24h price momentum   (max 15 pts) — Confirms trend is active today.
  Factor 3: Volume/MCap ratio    (max 18 pts) — High vol relative to size = genuine buying interest, not just large caps.
  Factor 4: Absolute volume      (max 10 pts) — Minimum liquidity gate. Low absolute volume = hard to exit.
  Factor 5: Market cap tier      (max  9 pts) — Mid-caps ($500M–$10B) score best: enough liquidity, still room to move.
  Factor 6: Trend alignment      (max  8 pts) — Bonus when 7d AND 24h are both positive = sustained directional move.
  Base: 18 pts
*/
function calcScore(coin) {
  const chg24 = coin.price_change_percentage_24h || 0;
  const chg7  = coin.price_change_percentage_7d_in_currency || 0;
  const vol   = coin.total_volume || 0;
  const mcap  = coin.market_cap || 1;
  const volMcap = vol / mcap;

  // Factor 1 — 7d trend (primary)
  const s7d = chg7 > 0 ? Math.min(chg7 * 2.0, 40) : chg7 * 4.0;

  // Factor 2 — 24h momentum
  const s24h = chg24 > 0 ? Math.min(chg24 * 1.2, 15) : Math.max(chg24 * 1.0, -12);

  // Factor 3 — Volume / Market cap ratio (unusual activity signal)
  // Sweet spot: 10–30% — above 50% often means a spike that may reverse
  let sVolRatio = 0;
  if (volMcap > 0.50) sVolRatio = 12;           // very high — spike risk, partial credit
  else if (volMcap > 0.25) sVolRatio = 18;       // exceptional sustained volume
  else if (volMcap > 0.12) sVolRatio = 14;       // high volume
  else if (volMcap > 0.06) sVolRatio = 9;        // moderate
  else if (volMcap > 0.02) sVolRatio = 4;        // low
  else sVolRatio = -4;                           // negligible volume = illiquid

  // Factor 4 — Absolute 24h volume (liquidity gate)
  // Below $50M daily volume → hard to enter/exit ₹2L without slippage
  let sAbsVol = 0;
  if (vol > 5e9)      sAbsVol = 10;   // >$5B — very liquid
  else if (vol > 1e9) sAbsVol = 8;    // >$1B
  else if (vol > 2e8) sAbsVol = 5;    // >$200M
  else if (vol > 5e7) sAbsVol = 2;    // >$50M — minimum acceptable
  else sAbsVol = -6;                  // below $50M — illiquid penalty

  // Factor 5 — Market cap tier
  // Mid-caps ($500M–$10B) have best risk/reward for short-term
  let sMcap = 0;
  if (mcap > 1e11)       sMcap = 3;   // mega cap (BTC/ETH) — moves slowly
  else if (mcap > 1e10)  sMcap = 7;   // large cap — decent
  else if (mcap > 2e9)   sMcap = 9;   // mid-large — sweet spot
  else if (mcap > 5e8)   sMcap = 8;   // mid — good
  else                   sMcap = 3;   // small cap — risky

  // Factor 6 — Trend alignment bonus (both 7d AND 24h positive)
  const sAlign = (chg7 > 0 && chg24 > 0) ? Math.min((chg7 + chg24) * 0.2, 8) : 0;

  return Math.min(99, Math.max(5, s7d + s24h + sVolRatio + sAbsVol + sMcap + sAlign + 18));
}

function buildReason(coin) {
  const chg24   = coin.price_change_percentage_24h || 0;
  const chg7    = coin.price_change_percentage_7d_in_currency || 0;
  const vol     = coin.total_volume || 0;
  const mcap    = coin.market_cap || 1;
  const volMcap = vol / mcap;
  const volB    = vol >= 1e9 ? "$" + (vol / 1e9).toFixed(1) + "B vol" : "$" + (vol / 1e6).toFixed(0) + "M vol";
  const mcapB   = mcap >= 1e9 ? "$" + (mcap / 1e9).toFixed(1) + "B mcap" : "$" + (mcap / 1e6).toFixed(0) + "M mcap";
  const reasons = [];

  // 7d signal
  if (chg7 > 30) reasons.push("Exceptional 7d rally +" + chg7.toFixed(1) + "%");
  else if (chg7 > 15) reasons.push("Strong 7d uptrend +" + chg7.toFixed(1) + "%");
  else if (chg7 > 5)  reasons.push("Positive 7d momentum +" + chg7.toFixed(1) + "%");
  else if (chg7 > 0)  reasons.push("Mild 7d gain +" + chg7.toFixed(1) + "%");

  // 24h signal
  if (chg24 > 12)     reasons.push("Surging today +" + chg24.toFixed(1) + "%");
  else if (chg24 > 4) reasons.push("Active today +" + chg24.toFixed(1) + "%");
  else if (chg24 > 0) reasons.push("Steady +" + chg24.toFixed(1) + "% today");

  // Volume signal
  if (volMcap > 0.30)      reasons.push("Exceptional buy pressure — " + (volMcap * 100).toFixed(0) + "% vol/mcap");
  else if (volMcap > 0.15) reasons.push("High volume activity — " + (volMcap * 100).toFixed(0) + "% vol/mcap");
  else if (volMcap > 0.08) reasons.push("Above-average volume — " + (volMcap * 100).toFixed(0) + "% vol/mcap");

  // Absolute volume
  if (vol > 2e9) reasons.push("Deep liquidity " + volB);
  else if (vol > 5e8) reasons.push("Good liquidity " + volB);

  // Trend alignment bonus
  if (chg7 > 0 && chg24 > 0) reasons.push("7d + 24h both positive — sustained trend");

  reasons.push(mcapB);
  return reasons.slice(0, 4).join(" · ");
}

function ScoreBar({ score }) {
  const color = score >= 70 ? "#4CAF7D" : score >= 50 ? "#F0A500" : "#E05C5C";
  return (
    <div style={{ background: "#1E2128", borderRadius: 3, height: 4, width: "100%" }}>
      <div style={{ width: Math.min(100, score) + "%", background: color, height: "100%", borderRadius: 3, transition: "width 0.8s ease" }} />
    </div>
  );
}


function MiniChart({ prices, color }) {
  if (!prices || prices.length < 2) return null;
  const vals = prices.map(p => p[1]);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const W = 340, H = 80;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * W;
    const y = H - ((v - min) / range) * (H - 8) - 4;
    return `${x},${y}`;
  }).join(" ");
  const first = vals[0], last = vals[vals.length - 1];
  const pct = ((last - first) / first * 100).toFixed(2);
  const up = last >= first;
  return (
    <div style={{ position: "relative" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 80 }}>
        <defs>
          <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
        <polygon points={`0,${H} ${pts} ${W},${H}`} fill="url(#cg)" />
      </svg>
      <div style={{ position: "absolute", top: 4, right: 6, fontSize: 11, fontWeight: 700, color: up ? "#4CAF7D" : "#E05C5C", fontFamily: "monospace" }}>
        {up ? "▲" : "▼"} {up ? "+" : ""}{pct}% (30d)
      </div>
    </div>
  );
}

function CoinDetailModal({ coinId, coinData, onClose }) {
  const [history, setHistory] = useState(null);
  const [ohlc, setOhlc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    if (!coinId) return;
    setLoading(true);
    setHistory(null);
    Promise.all([
      fetch(`${CG_BASE}/coins/${coinId}/market_chart?vs_currency=usd&days=${period}&interval=daily`),
      fetch(`${CG_BASE}/coins/${coinId}/ohlc?vs_currency=usd&days=30`)
    ]).then(async ([hRes, oRes]) => {
      if (hRes.ok) { const d = await hRes.json(); setHistory(d); }
      if (oRes.ok) { const d = await oRes.json(); setOhlc(d); }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [coinId, period]);

  if (!coinData) return null;
  const chg24 = coinData.price_change_percentage_24h || 0;
  const chg7  = coinData.price_change_percentage_7d_in_currency || 0;
  const volMcap = ((coinData.total_volume / coinData.market_cap) * 100).toFixed(1);
  const up = chg24 >= 0;
  const chartColor = up ? "#4CAF7D" : "#E05C5C";

  // Compute 30d stats from history
  let hiPrice = null, loPrice = null, avgVol = null;
  if (history?.prices?.length) {
    const prices = history.prices.map(p => p[1]);
    hiPrice = Math.max(...prices);
    loPrice = Math.min(...prices);
  }
  if (history?.total_volumes?.length) {
    const vols = history.total_volumes.map(v => v[1]);
    avgVol = vols.reduce((a, b) => a + b, 0) / vols.length;
  }

  // Recent OHLC rows (last 7)
  const recentOhlc = ohlc ? ohlc.slice(-7).reverse() : [];

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 1000,
      display: "flex", alignItems: "flex-end", justifyContent: "center"
    }} onClick={onClose}>
      <div style={{
        background: "#0D0F12", border: "1px solid #2A2D32", borderRadius: "16px 16px 0 0",
        width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto",
        padding: "0 0 32px"
      }} onClick={e => e.stopPropagation()}>

        {/* Modal header */}
        <div style={{ position: "sticky", top: 0, background: "#0D0F12", borderBottom: "1px solid #1A1C1F", padding: "14px 18px 12px", zIndex: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src={coinData.image} alt={coinData.symbol} style={{ width: 32, height: 32, borderRadius: "50%" }} onError={e => e.target.style.display = "none"} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: "#F0EEE6" }}>{coinData.symbol?.toUpperCase()} <span style={{ fontSize: 12, fontWeight: 400, color: "#555" }}>{coinData.name}</span></div>
              <div style={{ fontSize: 13, fontFamily: "monospace", color: up ? "#4CAF7D" : "#E05C5C", fontWeight: 700 }}>
                {fmt(coinData.current_price)} &nbsp;
                <span style={{ fontSize: 11 }}>{fmtPct(chg24)} today</span>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "#1A1C1F", border: "1px solid #2A2D32", borderRadius: 8, color: "#888", fontSize: 16, padding: "4px 10px", cursor: "pointer" }}>✕</button>
          </div>

          {/* Period selector */}
          <div style={{ display: "flex", gap: 5, marginTop: 10 }}>
            {[[7,"7d"],[14,"14d"],[30,"30d"],[90,"90d"]].map(([d, l]) => (
              <button key={d} onClick={() => setPeriod(d)} style={{
                background: period === d ? "#4CAF7D" : "#1A1C1F",
                color: period === d ? "#000" : "#666",
                border: "1px solid " + (period === d ? "#4CAF7D" : "#2A2D32"),
                borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer"
              }}>{l}</button>
            ))}
          </div>
        </div>

        <div style={{ padding: "0 18px" }}>

          {/* Price chart */}
          <div style={{ marginTop: 14, background: "#111316", border: "1px solid #1E2128", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ padding: "8px 12px 4px", fontSize: 10, color: "#555", fontWeight: 600, letterSpacing: "0.06em" }}>PRICE CHART ({period}D)</div>
            {loading ? (
              <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#333", fontSize: 12 }}>Loading...</div>
            ) : history?.prices ? (
              <MiniChart prices={history.prices} color={chartColor} />
            ) : (
              <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center", color: "#333", fontSize: 12 }}>No data</div>
            )}
          </div>

          {/* Key stats grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 12 }}>
            {[
              ["24h Change", fmtPct(chg24), chg24 >= 0 ? "#4CAF7D" : "#E05C5C"],
              ["7d Change",  fmtPct(chg7),  chg7  >= 0 ? "#4CAF7D" : "#E05C5C"],
              ["Vol/MCap",   volMcap + "%",  parseFloat(volMcap) > 10 ? "#F0A500" : "#78AADF"],
              ["Market Cap", fmt(coinData.market_cap), "#C8C6BE"],
              ["24h Volume", fmt(coinData.total_volume), "#C8C6BE"],
              ["ATH",        fmt(coinData.ath), "#F0A500"],
              ["ATH Drop",   fmtPct(coinData.ath_change_percentage), "#E05C5C"],
              ["Rank",       "#" + (coinData.market_cap_rank || "—"), "#78AADF"],
              ["Circulating",coinData.circulating_supply ? (coinData.circulating_supply / 1e6).toFixed(1) + "M" : "—", "#888"],
            ].map(([l, v, col]) => (
              <div key={l} style={{ background: "#111316", border: "1px solid #1A1C1F", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 9, color: "#444", marginBottom: 2 }}>{l}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: col, fontFamily: "monospace" }}>{v || "—"}</div>
              </div>
            ))}
          </div>

          {/* 30d price range */}
          {hiPrice && loPrice && (
            <div style={{ marginTop: 12, background: "#111316", border: "1px solid #1A1C1F", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 10, color: "#555", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8 }}>{period}D PRICE RANGE</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: 11, color: "#E05C5C", fontFamily: "monospace" }}>Low: {fmt(loPrice)}</span>
                <span style={{ fontSize: 11, color: "#4CAF7D", fontFamily: "monospace" }}>High: {fmt(hiPrice)}</span>
              </div>
              <div style={{ background: "#1A1C1F", borderRadius: 4, height: 6, position: "relative" }}>
                {(() => {
                  const cur = coinData.current_price;
                  const pct = Math.min(100, Math.max(0, ((cur - loPrice) / (hiPrice - loPrice)) * 100));
                  return <div style={{ position: "absolute", left: pct + "%", top: -3, width: 12, height: 12, background: "#F0A500", borderRadius: "50%", transform: "translateX(-50%)", border: "2px solid #0D0F12" }} />;
                })()}
                <div style={{ width: "100%", height: "100%", background: "linear-gradient(90deg, #E05C5C22, #4CAF7D22)", borderRadius: 4 }} />
              </div>
              <div style={{ fontSize: 10, color: "#555", marginTop: 6, textAlign: "center" }}>
                Current {fmt(coinData.current_price)} · {(((coinData.current_price - loPrice) / (hiPrice - loPrice)) * 100).toFixed(0)}% from low
              </div>
            </div>
          )}

          {/* Volume trend */}
          {history?.total_volumes?.length > 0 && (
            <div style={{ marginTop: 12, background: "#111316", border: "1px solid #1A1C1F", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 10, color: "#555", fontWeight: 700, letterSpacing: "0.06em", marginBottom: 8 }}>VOLUME TREND ({period}D)</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 40 }}>
                {history.total_volumes.slice(-14).map((v, i) => {
                  const vols = history.total_volumes.slice(-14).map(x => x[1]);
                  const maxV = Math.max(...vols);
                  const h = Math.max(4, (v[1] / maxV) * 40);
                  const isLast3 = i >= vols.length - 3;
                  return <div key={i} style={{ flex: 1, height: h, background: isLast3 ? "#F0A500" : "#2A2D32", borderRadius: "2px 2px 0 0" }} />;
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: 9, color: "#444" }}>{period}d ago</span>
                <span style={{ fontSize: 9, color: "#F0A500" }}>recent ▶</span>
              </div>
              {avgVol && <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>Avg daily vol: {fmt(avgVol)}</div>}
            </div>
          )}

          {/* Recent OHLC table */}
          {recentOhlc.length > 0 && (
            <div style={{ marginTop: 12, background: "#111316", border: "1px solid #1A1C1F", borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", fontSize: 10, color: "#555", fontWeight: 700, letterSpacing: "0.06em" }}>RECENT DAILY OHLC</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10 }}>
                  <thead>
                    <tr style={{ background: "#0A0C10" }}>
                      {["Date", "Open", "High", "Low", "Close"].map(h => (
                        <th key={h} style={{ padding: "5px 8px", color: "#444", fontWeight: 600, textAlign: "right", borderBottom: "1px solid #1A1C1F" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentOhlc.map((row, i) => {
                      const [ts, o, h, l, cl] = row;
                      const date = new Date(ts).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
                      const bullish = cl >= o;
                      return (
                        <tr key={i} style={{ borderBottom: "1px solid #111316" }}>
                          <td style={{ padding: "5px 8px", color: "#555", textAlign: "right" }}>{date}</td>
                          <td style={{ padding: "5px 8px", color: "#888", fontFamily: "monospace", textAlign: "right" }}>{fmt(o)}</td>
                          <td style={{ padding: "5px 8px", color: "#4CAF7D", fontFamily: "monospace", textAlign: "right" }}>{fmt(h)}</td>
                          <td style={{ padding: "5px 8px", color: "#E05C5C", fontFamily: "monospace", textAlign: "right" }}>{fmt(l)}</td>
                          <td style={{ padding: "5px 8px", color: bullish ? "#4CAF7D" : "#E05C5C", fontFamily: "monospace", fontWeight: 700, textAlign: "right" }}>{fmt(cl)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CoinGecko link */}
          <div style={{ marginTop: 14, textAlign: "center" }}>
            <a href={"https://www.coingecko.com/en/coins/" + coinId} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 11, color: "#3A6A8A", textDecoration: "none", borderBottom: "1px solid #1A3A4A", paddingBottom: 2 }}>
              View full data on CoinGecko ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function StrategySection({ coins }) {
  const [selectedCoin, setSelectedCoin] = useState(null);
  // Pick top 5 by momentum score, excluding stablecoins
  const stables = ["tether", "usd-coin", "binance-usd", "dai", "true-usd", "frax"];
  const eligible = coins
    .filter(c =>
      !stables.includes(c.id)
      && c.market_cap > 5e8                                          // min $500M market cap
      && c.total_volume > 5e7                                        // min $50M daily volume — liquidity gate
      && (c.price_change_percentage_7d_in_currency || 0) > 0        // must have positive 7d trend
      && (c.price_change_percentage_24h || 0) > -5                  // not crashing today (max -5% 24h)
    )
    .map(c => ({ ...c, score: calcScore(c) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 5);

  if (!eligible.length) return null;

  const allocations = eligible.map((c, i) => ({
    ...c,
    weight: STRATEGY_WEIGHTS[i],
    inr: Math.round(CAPITAL * STRATEGY_WEIGHTS[i]),
    risk: RISK_MAP[c.id] || "Medium-High",
  }));

  const bestCase = allocations.reduce((sum, c) => {
    const tp = TP2[c.risk] || 150;
    const gain = c.inr * tp / 100;
    const tax = gain * 0.30;
    return sum + c.inr + gain - tax;
  }, 0);

  const goodCase = allocations.reduce((sum, c) => {
    const tp = TP1[c.risk] || 50;
    const gain = c.inr * tp / 100;
    const tax = gain * 0.30;
    return sum + c.inr + gain - tax;
  }, 0);

  const worstCase = allocations.reduce((sum, c) => {
    const sl = STOP_LOSS[c.risk] || 20;
    return sum + c.inr * (1 - sl / 100);
  }, 0);

  return (
    <>
    <div style={{ margin: "14px 18px 0" }}>
      <div style={{ background: "#0D1A0D", border: "1px solid #1A3A1A", borderRadius: 14, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #0D1A0D, #112211)", padding: "14px 16px", borderBottom: "1px solid #1A3A1A" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#4CAF7D", letterSpacing: "0.1em" }}>◈ LIVE STRATEGY — ₹2,00,000 CAPITAL</div>
              <div style={{ fontSize: 11, color: "#3A6A3A", marginTop: 2 }}>Auto-selected top 5 by momentum score · Updates on refresh</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {[["Worst case", fmtINR(worstCase), "#E05C5C"], ["Good month", fmtINR(goodCase), "#F0A500"], ["Bull run", fmtINR(bestCase), "#4CAF7D"]].map(([l, v, c]) => (
                <div key={l} style={{ background: "#0A120A", border: "1px solid #1A3A1A", borderRadius: 8, padding: "6px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "#3A6A3A" }}>{l}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "monospace" }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Coin rows */}
        {allocations.map((c, i) => {
          const risk = c.risk;
          const sl = STOP_LOSS[risk] || 20;
          const tp1 = TP1[risk] || 50;
          const tp2 = TP2[risk] || 100;
          const rColor = RISK_COLOR[risk] || "#F0A500";
          const chg24 = c.price_change_percentage_24h || 0;
          const chg7 = c.price_change_percentage_7d_in_currency || 0;
          return (
            <div key={c.id} onClick={() => setSelectedCoin(c.id === selectedCoin ? null : c.id)}
              style={{ padding: "12px 16px", borderBottom: i < 4 ? "1px solid #112211" : "none", background: i % 2 === 0 ? "transparent" : "#080E08", cursor: "pointer", transition: "background 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#0D1F0D"}
              onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : "#080E08"}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#3A6A3A", width: 16 }}>{i + 1}</div>
                <img src={c.image} alt={c.symbol} style={{ width: 24, height: 24, borderRadius: "50%" }} onError={e => e.target.style.display = "none"} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#D0EED0" }}>{c.symbol.toUpperCase()}</span>
                    <span style={{ fontSize: 9, color: "#2A5A2A", marginLeft: 2 }}>tap for history</span>
                    <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 999, background: RISK_BG[risk], color: rColor }}>{risk} risk</span>
                    {chg24 > 5 && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 999, background: "#0A1F0A", color: "#4CAF7D" }}>↑ {fmtPct(chg24)}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: "#3A5A3A", marginTop: 1 }}>{NARRATIVES[c.id] || "Altcoin"}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#4CAF7D", fontFamily: "monospace" }}>{fmtINR(c.inr)}</div>
                  <div style={{ fontSize: 10, color: "#3A6A3A" }}>{Math.round(c.weight * 100)}% of capital</div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 4 }}>
                {[
                  ["Price", fmt(c.current_price), "#C8C6BE"],
                  ["7d", fmtPct(chg7), chg7 >= 0 ? "#4CAF7D" : "#E05C5C"],
                  ["Stop", "-" + sl + "%", "#E05C5C"],
                  ["TP1", "+" + tp1 + "%", "#F0A500"],
                  ["TP2", "+" + tp2 + "%", "#4CAF7D"],
                ].map(([l, v, col]) => (
                  <div key={l} style={{ background: "#060E06", borderRadius: 6, padding: "5px 7px", textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: "#3A5A3A" }}>{l}</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: col, fontFamily: "monospace" }}>{v}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 9, color: "#3A5A3A" }}>Momentum score</span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: c.score >= 70 ? "#4CAF7D" : c.score >= 50 ? "#F0A500" : "#E05C5C" }}>{Math.round(c.score)}/99</span>
                </div>
                <ScoreBar score={c.score} />
              </div>
              <div style={{ marginTop: 7, background: "#060E06", borderRadius: 6, padding: "7px 9px" }}>
                <div style={{ fontSize: 9, color: "#3A5A3A", marginBottom: 4, fontWeight: 700, letterSpacing: "0.06em" }}>WHY SELECTED</div>
                <div style={{ fontSize: 10, color: "#5A8A5A", lineHeight: 1.7 }}>{buildReason(c)}</div>
                <div style={{ marginTop: 5, display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {[
                    ["7d", (c.price_change_percentage_7d_in_currency||0).toFixed(1)+"%", (c.price_change_percentage_7d_in_currency||0) >= 0 ? "#4CAF7D" : "#E05C5C"],
                    ["24h", (c.price_change_percentage_24h||0).toFixed(1)+"%", (c.price_change_percentage_24h||0) >= 0 ? "#4CAF7D" : "#E05C5C"],
                    ["Vol/MC", ((c.total_volume/c.market_cap)*100).toFixed(1)+"%", (c.total_volume/c.market_cap) > 0.1 ? "#F0A500" : "#666"],
                    ["Liq", c.total_volume >= 1e9 ? "$"+(c.total_volume/1e9).toFixed(1)+"B" : "$"+(c.total_volume/1e6).toFixed(0)+"M", c.total_volume > 5e8 ? "#78AADF" : "#666"],
                  ].map(([l, v, col]) => (
                    <div key={l} style={{ background: "#0A120A", border: "1px solid #1A2A1A", borderRadius: 5, padding: "3px 7px", textAlign: "center" }}>
                      <div style={{ fontSize: 8, color: "#3A5A3A" }}>{l}</div>
                      <div style={{ fontSize: 9, fontWeight: 700, color: col, fontFamily: "monospace" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        {/* Footer note */}
        <div style={{ padding: "10px 16px", background: "#060E06", borderTop: "1px solid #112211" }}>
          <div style={{ fontSize: 10, color: "#2A4A2A", lineHeight: 1.6 }}>
            TP1 = Take profit 40% of position · TP2 = Take profit remaining 30% · Stop-loss triggers auto-exit · Tax: 30% on gains (India) · Not financial advice
          </div>
        </div>
      </div>
    </div>
      {selectedCoin && (
        <CoinDetailModal
          coinId={selectedCoin}
          coinData={coins.find(c => c.id === selectedCoin)}
          onClose={() => setSelectedCoin(null)}
        />
      )}
    </>
  );
}

function CoinCard({ coin, rank, onSelect }) {
  const chg24 = coin.price_change_percentage_24h || 0;
  const chg7 = coin.price_change_percentage_7d_in_currency || 0;
  const volMcap = (coin.total_volume / coin.market_cap) || 0;
  const score = calcScore(coin);
  const risk = RISK_MAP[coin.id] || "Medium-High";
  const rColor = RISK_COLOR[risk] || "#F0A500";
  const rBg = RISK_BG[risk] || "#2A1A00";
  const narrative = NARRATIVES[coin.id] || "Altcoin";

  return (
    <div onClick={() => onSelect && onSelect(coin)}
      style={{ background: "#1A1C1F", border: "1px solid #2A2D32", borderRadius: 12, padding: "12px 14px", marginBottom: 8, position: "relative", cursor: "pointer", transition: "border-color 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "#3A4A5A"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "#2A2D32"}
    >
      <div style={{ position: "absolute", top: 12, right: 12, fontFamily: "monospace", fontSize: 10, color: "#444" }}>#{rank}</div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <img src={coin.image} alt={coin.symbol} style={{ width: 26, height: 26, borderRadius: "50%" }} onError={e => e.target.style.display = "none"} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#F0EEE6" }}>{coin.symbol.toUpperCase()}</span>
            <span style={{ fontSize: 9, color: "#2A4A6A" }}>tap for history</span>
          </div>
          <div style={{ fontSize: 10, color: "#555" }}>{coin.name}</div>
        </div>
        <div style={{ textAlign: "right", paddingRight: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#F0EEE6", fontFamily: "monospace" }}>{fmt(coin.current_price)}</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: chg24 >= 0 ? "#4CAF7D" : "#E05C5C", fontFamily: "monospace" }}>{fmtPct(chg24)}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 999, background: "#1A2A3A", color: "#78AADF" }}>{narrative}</span>
        <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 999, background: rBg, color: rColor }}>{risk}</span>
        {chg7 > 20 && <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 999, background: "#0A1F0A", color: "#4CAF7D" }}>🔥 Strong 7d</span>}
        {volMcap > 0.15 && <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 999, background: "#1F1400", color: "#F0A500" }}>⚡ High Vol</span>}
        {chg24 > 10 && <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 999, background: "#0A1F0A", color: "#4CAF7D" }}>↑ Pumping</span>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 5, marginBottom: 8 }}>
        {[
          ["7d", fmtPct(chg7), chg7 >= 0 ? "#4CAF7D" : "#E05C5C"],
          ["MCap", fmt(coin.market_cap), "#888"],
          ["Volume", fmt(coin.total_volume), "#888"],
          ["Vol/MC", (volMcap * 100).toFixed(1) + "%", volMcap > 0.1 ? "#F0A500" : "#666"],
        ].map(([l, v, c]) => (
          <div key={l} style={{ background: "#111316", borderRadius: 6, padding: "6px 8px" }}>
            <div style={{ fontSize: 9, color: "#555", marginBottom: 1 }}>{l}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: c, fontFamily: "monospace" }}>{v}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: 9, color: "#555" }}>Momentum</span>
        <span style={{ fontSize: 9, fontWeight: 700, color: score >= 70 ? "#4CAF7D" : score >= 50 ? "#F0A500" : "#E05C5C" }}>{Math.round(score)}/99</span>
      </div>
      <ScoreBar score={score} />
    </div>
  );
}

export default function App() {
  const [coins, setCoins] = useState([]);
  const [trending, setTrending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("strategy");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [globalSelected, setGlobalSelected] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [marketRes, trendRes] = await Promise.all([
        fetch(`${CG_BASE}/coins/markets?vs_currency=usd&order=volume_desc&per_page=100&page=1&sparkline=false&price_change_percentage=7d&locale=en`),
        fetch(`${CG_BASE}/search/trending`)
      ]);
      if (!marketRes.ok) throw new Error("Rate limit hit — wait 60s and retry");
      const [marketData, trendData] = await Promise.all([marketRes.json(), trendRes.json()]);
      setCoins(marketData);
      setTrending(trendData.coins || []);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stables = ["tether", "usd-coin", "binance-usd", "dai", "true-usd", "frax", "usdd", "tusd"];
  const nonStable = coins.filter(c => !stables.includes(c.id));

  const topGainers = [...nonStable].sort((a, b) => (b.price_change_percentage_24h || 0) - (a.price_change_percentage_24h || 0)).slice(0, 20);
  const highVolume = [...nonStable].filter(c => (c.total_volume / c.market_cap) > 0.05).sort((a, b) => (b.total_volume / b.market_cap) - (a.total_volume / a.market_cap)).slice(0, 20);
  const top7d = [...nonStable].sort((a, b) => (b.price_change_percentage_7d_in_currency || 0) - (a.price_change_percentage_7d_in_currency || 0)).slice(0, 20);

  const tabs = [
    ["strategy", "⚡ Strategy"],
    ["gainers", "📈 Gainers"],
    ["volume", "🔥 High Vol"],
    ["weekly", "📊 Top 7d"],
    ["trending", "✨ Trending"],
  ];

  const gainersCount = nonStable.filter(c => (c.price_change_percentage_24h || 0) > 0).length;
  const losersCount = nonStable.filter(c => (c.price_change_percentage_24h || 0) < 0).length;
  const avgChg = nonStable.length ? (nonStable.reduce((s, c) => s + (c.price_change_percentage_24h || 0), 0) / nonStable.length).toFixed(2) : "—";

  return (
    <div style={{ background: "#0D0F12", minHeight: "100vh", fontFamily: "'Inter', -apple-system, sans-serif", color: "#F0EEE6", paddingBottom: 40 }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0A0C10 0%, #111620 100%)", borderBottom: "1px solid #1E2128", padding: "14px 18px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: -0.5, color: "#F0EEE6" }}>
              <span style={{ color: "#F0A500" }}>◈</span> CryptoLens
            </div>
            <div style={{ fontSize: 11, color: "#444", marginTop: 1 }}>Real-time market scanner</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {lastUpdated && <span style={{ fontSize: 10, color: "#444" }}>↻ {lastUpdated.toLocaleTimeString()}</span>}
            <button onClick={fetchData} disabled={loading} style={{
              background: "#1A2A3A", color: loading ? "#444" : "#78AADF",
              border: "1px solid #2A3A4A", borderRadius: 8, padding: "7px 14px",
              fontSize: 12, cursor: loading ? "not-allowed" : "pointer", fontWeight: 600
            }}>
              {loading ? "⟳ Loading..." : "⟳ Refresh"}
            </button>
          </div>
        </div>

        {/* Market stats */}
        {coins.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            {[
              ["Gainers", gainersCount, "#4CAF7D"],
              ["Losers", losersCount, "#E05C5C"],
              ["Avg 24h", (avgChg >= 0 ? "+" : "") + avgChg + "%", avgChg >= 0 ? "#4CAF7D" : "#E05C5C"],
              ["Trending", trending.length, "#F0A500"],
              ["Coins", coins.length, "#78AADF"],
            ].map(([l, v, c]) => (
              <div key={l} style={{ background: "#1A1C1F", borderRadius: 8, padding: "5px 11px", border: "1px solid #2A2D32" }}>
                <div style={{ fontSize: 9, color: "#444" }}>{l}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div style={{ margin: "10px 18px", background: "#1A0A0A", border: "1px solid #3A1A1A", borderRadius: 8, padding: "9px 13px", fontSize: 12, color: "#E05C5C" }}>
          ⚠ {error} — CoinGecko free API rate limit. Wait 60s and refresh.
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, margin: "12px 18px 0", background: "#1A1C1F", borderRadius: 10, padding: 3, overflowX: "auto" }}>
        {tabs.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            flex: "0 0 auto", background: tab === key ? "#2A2D32" : "transparent",
            color: tab === key ? "#F0EEE6" : "#555", border: "none", borderRadius: 8,
            padding: "8px 12px", fontSize: 11, fontWeight: tab === key ? 700 : 400, cursor: "pointer", whiteSpace: "nowrap"
          }}>{label}</button>
        ))}
      </div>

      {/* Content */}
      {loading && !coins.length ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#444" }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>◈</div>
          <div style={{ fontSize: 13 }}>Fetching live market data...</div>
        </div>
      ) : (
        <>
          {tab === "strategy" && <StrategySection coins={nonStable} />}

          {tab === "gainers" && (
            <div style={{ padding: "10px 18px 0" }}>
              <div style={{ fontSize: 11, color: "#444", marginBottom: 10 }}>Top 20 gainers by 24h price change</div>
              {topGainers.map((c, i) => <CoinCard key={c.id} coin={c} rank={i + 1} onSelect={coin => setGlobalSelected(coin)} />)}
            </div>
          )}

          {tab === "volume" && (
            <div style={{ padding: "10px 18px 0" }}>
              <div style={{ fontSize: 11, color: "#444", marginBottom: 10 }}>Highest volume-to-market-cap ratio — signals unusual activity</div>
              {highVolume.map((c, i) => <CoinCard key={c.id} coin={c} rank={i + 1} onSelect={coin => setGlobalSelected(coin)} />)}
            </div>
          )}

          {tab === "weekly" && (
            <div style={{ padding: "10px 18px 0" }}>
              <div style={{ fontSize: 11, color: "#444", marginBottom: 10 }}>Top performers over the past 7 days</div>
              {top7d.map((c, i) => <CoinCard key={c.id} coin={c} rank={i + 1} onSelect={coin => setGlobalSelected(coin)} />)}
            </div>
          )}

          {tab === "trending" && (
            <div style={{ padding: "10px 18px 0" }}>
              <div style={{ fontSize: 11, color: "#444", marginBottom: 10 }}>Trending on CoinGecko right now</div>
              {trending.map((t, i) => {
                const item = t.item;
                if (!item) return null;
                return (
                  <div key={i}
                    onClick={() => setGlobalSelected({ id: item.id, name: item.name, symbol: item.symbol, image: item.large || item.thumb, current_price: item.data?.price, price_change_percentage_24h: item.data?.price_change_percentage_24h?.usd, market_cap: item.data?.market_cap, total_volume: item.data?.total_volume, ath: null, ath_change_percentage: null, market_cap_rank: item.market_cap_rank, circulating_supply: null })}
                    style={{ background: "#1A1C1F", border: "1px solid #2A2D32", borderRadius: 12, padding: "12px 14px", marginBottom: 8, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", transition: "border-color 0.15s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "#3A4A5A"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "#2A2D32"}
                  >
                    <div style={{ fontFamily: "monospace", fontSize: 10, color: "#444", width: 18 }}>#{i + 1}</div>
                    <img src={item.large || item.thumb} alt={item.symbol} style={{ width: 26, height: 26, borderRadius: "50%" }} onError={e => e.target.style.display = "none"} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#F0EEE6" }}>{item.symbol}</span>
                        <span style={{ fontSize: 9, color: "#2A4A6A" }}>tap for history</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#555" }}>{item.name}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 11, color: "#F0A500", fontWeight: 600 }}>Rank #{item.market_cap_rank || "—"}</div>
                      <div style={{ fontSize: 10, color: "#444" }}>Trend #{item.score + 1}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <div style={{ padding: "20px 18px 0", textAlign: "center" }}>
        <div style={{ fontSize: 9, color: "#2A2D32", lineHeight: 1.7 }}>
          Data: CoinGecko Public API · Auto-refreshed · Not financial advice · 30% tax on gains in India · stocknote.co
        </div>
      </div>

      {globalSelected && (
        <CoinDetailModal
          coinId={globalSelected.id}
          coinData={globalSelected}
          onClose={() => setGlobalSelected(null)}
        />
      )}
    </div>
  );
}